/**
 * GlobalCallPopup.jsx — WhatsApp-style floating call card.
 *
 * Mounted at App.jsx root (outside <BrowserRouter>) so it survives
 * all route changes and stays visible on every page.
 *
 * Communication pattern:
 *   TelephonyContext  ──window.CustomEvent──►  GlobalCallPopup
 *   GlobalCallPopup   ──window.CustomEvent──►  TelephonyContext
 *
 * This is the same "window event bus" pattern already used by
 * `telephony:presence-changed` in the existing codebase.
 *
 * States:
 *   IDLE              → nothing rendered
 *   RINGING           → inbound call — Answer + Decline buttons, pulse ring
 *   OUTBOUND_CALLING  → outbound call — "Calling..." + Cancel
 *   CONNECTED         → live timer + Mute/Hold/End + "Open Call Center" link
 *   MINIMIZED         → draggable pill (click to restore)
 *   MISSED            → brief 3s "Missed Call" flash then IDLE
 */

import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import './GlobalCallPopup.css'

// ── Helpers ──────────────────────────────────────────────────────────────────

const POPUP_STATES = {
  IDLE:             'IDLE',
  RINGING:          'RINGING',
  OUTBOUND_CALLING: 'OUTBOUND_CALLING',
  CONNECTED:        'CONNECTED',
  MINIMIZED:        'MINIMIZED',
  MISSED:           'MISSED',
}

function formatDuration(secs) {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function getInitials(name = '') {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase() || '?'
}

function dispatch(eventName, detail = {}) {
  window.dispatchEvent(new CustomEvent(eventName, { detail }))
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function GlobalCallPopup() {
  const navigate = useNavigate()

  // ── Popup state ────────────────────────────────────────────────────────────
  const [popupState, setPopupState] = useState(POPUP_STATES.IDLE)
  const [callerInfo, setCallerInfo]  = useState(null)
  // { callerName, callerNumber, callSid, direction }

  const [isMuted,  setIsMuted]  = useState(false)
  const [isOnHold, setIsOnHold] = useState(false)

  // ── Call timer (runs only in CONNECTED / MINIMIZED states) ────────────────
  const [elapsed, setElapsed] = useState(0)
  const timerRef = useRef(null)

  const startTimer = useCallback(() => {
    setElapsed(0)
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000)
  }, [])

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setElapsed(0)
  }, [])

  useEffect(() => () => stopTimer(), [stopTimer])

  // ── Missed-call auto-hide timer ref ───────────────────────────────────────
  const missedTimerRef = useRef(null)

  const resetToIdle = useCallback(() => {
    setPopupState(POPUP_STATES.IDLE)
    setCallerInfo(null)
    setIsMuted(false)
    setIsOnHold(false)
    stopTimer()
    if (missedTimerRef.current) {
      clearTimeout(missedTimerRef.current)
      missedTimerRef.current = null
    }
  }, [stopTimer])

  // ── "Previous state before minimize" — so we know which card to restore ──
  const preMinimizeState = useRef(POPUP_STATES.RINGING)

  // ── Listen to TelephonyContext window events ───────────────────────────────
  useEffect(() => {
    // ── Inbound: RINGING ────────────────────────────────────────────────────
    const onIncoming = (e) => {
      const { callerName, callerNumber, callSid, direction } = e.detail || {}
      setCallerInfo({ callerName, callerNumber, callSid, direction: direction || 'inbound' })
      setIsMuted(false)
      setIsOnHold(false)
      stopTimer()
      setPopupState(POPUP_STATES.RINGING)
      if (missedTimerRef.current) clearTimeout(missedTimerRef.current)
    }

    // ── Outbound: CALLING ───────────────────────────────────────────────────
    const onOutboundStarted = (e) => {
      const { callerName, callerNumber, callSid } = e.detail || {}
      setCallerInfo({ callerName, callerNumber, callSid, direction: 'outbound' })
      setIsMuted(false)
      setIsOnHold(false)
      stopTimer()
      setPopupState(POPUP_STATES.OUTBOUND_CALLING)
      if (missedTimerRef.current) clearTimeout(missedTimerRef.current)
    }

    // ── Call connected ──────────────────────────────────────────────────────
    const onConnected = (e) => {
      // Enrich caller info if context provides updated data
      if (e.detail?.callerInfo) {
        const ci = e.detail.callerInfo
        setCallerInfo((prev) => ({
          ...prev,
          callerName: ci.customer_name || ci.contact_name || prev?.callerName,
          callerNumber: ci.from || prev?.callerNumber,
          direction: ci.direction || prev?.direction,
        }))
      }
      startTimer()
      setPopupState(POPUP_STATES.CONNECTED)
    }

    // ── Call ended ──────────────────────────────────────────────────────────
    const onEnded = () => resetToIdle()

    // ── Call missed ─────────────────────────────────────────────────────────
    const onMissed = () => {
      setPopupState(POPUP_STATES.MISSED)
      stopTimer()
      // Auto-hide after 3 seconds
      if (missedTimerRef.current) clearTimeout(missedTimerRef.current)
      missedTimerRef.current = setTimeout(() => {
        resetToIdle()
      }, 3000)
    }

    // ── Caller info enriched ────────────────────────────────────────────────
    const onCallerInfoUpdated = (e) => {
      const { callerName, projectName, callerNumber } = e.detail || {}
      setCallerInfo((prev) => ({
        ...prev,
        callerName: callerName || prev?.callerName,
        projectName: projectName || prev?.projectName,
        callerNumber: callerNumber || prev?.callerNumber,
      }))
    }

    window.addEventListener('telephony:incoming-call',        onIncoming)
    window.addEventListener('telephony:outbound-call-started', onOutboundStarted)
    window.addEventListener('telephony:caller-info-updated',   onCallerInfoUpdated)
    window.addEventListener('telephony:call-connected',       onConnected)
    window.addEventListener('telephony:call-ended',           onEnded)
    window.addEventListener('telephony:call-missed',          onMissed)

    return () => {
      window.removeEventListener('telephony:incoming-call',        onIncoming)
      window.removeEventListener('telephony:outbound-call-started', onOutboundStarted)
      window.removeEventListener('telephony:caller-info-updated',   onCallerInfoUpdated)
      window.removeEventListener('telephony:call-connected',       onConnected)
      window.removeEventListener('telephony:call-ended',           onEnded)
      window.removeEventListener('telephony:call-missed',          onMissed)
    }
  }, [startTimer, stopTimer, resetToIdle])

  // ── Action handlers (dispatch back to TelephonyContext) ───────────────────

  const handleAnswer = () => dispatch('telephony:answer-call')
  const handleDecline = () => {
    dispatch('telephony:decline-call')
    resetToIdle()
  }
  const handleCancelOutbound = () => {
    dispatch('telephony:cancel-outbound-call')
    resetToIdle()
  }
  const handleEnd = () => {
    dispatch('telephony:end-call')
    resetToIdle()
  }
  const handleToggleMute = () => {
    const next = !isMuted
    setIsMuted(next)
    dispatch('telephony:toggle-mute', { muted: next })
  }
  const handleToggleHold = () => {
    const next = !isOnHold
    setIsOnHold(next)
    dispatch('telephony:toggle-hold', { hold: next })
  }

  const handleMinimize = () => {
    preMinimizeState.current = popupState
    setPopupState(POPUP_STATES.MINIMIZED)
  }
  const handleExpand = () => {
    setPopupState(preMinimizeState.current || POPUP_STATES.CONNECTED)
  }

  const handleOpenCallCenter = () => {
    navigate('/loop/call-center')
  }

  // ── Derived display values ────────────────────────────────────────────────
  const name   = callerInfo?.callerName   || 'Unknown'
  const number = callerInfo?.callerNumber || ''
  const initials = getInitials(name)

  // ── Nothing to show ───────────────────────────────────────────────────────
  if (popupState === POPUP_STATES.IDLE) return null

  // ── MINIMIZED pill ─────────────────────────────────────────────────────────
  if (popupState === POPUP_STATES.MINIMIZED) {
    return (
      <div className="gcp-pill" onClick={handleExpand} title="Expand call">
        {preMinimizeState.current === POPUP_STATES.RINGING || preMinimizeState.current === POPUP_STATES.OUTBOUND_CALLING
          ? <div className="gcp-pill-ringing-dot" />
          : <div className="gcp-pill-dot" />
        }
        <span className="gcp-pill-text">{name}</span>
        {preMinimizeState.current === POPUP_STATES.CONNECTED && (
          <span className="gcp-pill-timer">{formatDuration(elapsed)}</span>
        )}
        <span className="gcp-pill-expand material-symbols-outlined">open_in_full</span>
      </div>
    )
  }

  // ── Full card ──────────────────────────────────────────────────────────────
  const cardClass = {
    [POPUP_STATES.RINGING]:          'gcp-card--ringing',
    [POPUP_STATES.OUTBOUND_CALLING]: 'gcp-card--calling',
    [POPUP_STATES.CONNECTED]:        'gcp-card--connected',
    [POPUP_STATES.MISSED]:           'gcp-card--missed',
  }[popupState] || ''

  const directionLabel = {
    [POPUP_STATES.RINGING]:          '↙ Incoming Call',
    [POPUP_STATES.OUTBOUND_CALLING]: '↗ Outgoing Call',
    [POPUP_STATES.CONNECTED]:        callerInfo?.direction === 'outbound' ? '↗ Outgoing · Active' : '↙ Incoming · Active',
    [POPUP_STATES.MISSED]:           '✕ Missed Call',
  }[popupState] || ''

  return (
    <div className="gcp-root">
      <div className={`gcp-card ${cardClass}`}>

        {/* Top action buttons */}
        {popupState !== POPUP_STATES.MISSED && (
          <div className="gcp-top-actions">
            <button
              type="button"
              className="gcp-icon-btn"
              onClick={handleMinimize}
              title="Minimize"
              aria-label="Minimize call popup"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                remove
              </span>
            </button>
          </div>
        )}

        {/* ── Header: avatar + caller info ── */}
        <div className="gcp-header">
          <div className="gcp-avatar-wrap">
            {popupState === POPUP_STATES.RINGING && (
              <>
                <div className="gcp-pulse-ring" />
                <div className="gcp-pulse-ring" />
                <div className="gcp-pulse-ring" />
              </>
            )}
            <div className="gcp-avatar">{initials}</div>
          </div>

          <div className="gcp-caller-info">
            <p className="gcp-direction-label">{directionLabel}</p>
            <p className="gcp-caller-name" title={name}>{name}</p>
            {callerInfo?.projectName && (
              <p className="gcp-caller-sub text-xs text-slate-500 font-medium truncate max-w-[200px]" title={callerInfo.projectName}>
                📁 {callerInfo.projectName}
              </p>
            )}
            {number && <p className="gcp-caller-number">{number}</p>}
          </div>
        </div>

        {/* ── RINGING: status + Answer/Decline ── */}
        {popupState === POPUP_STATES.RINGING && (
          <>
            <p className="gcp-status-line">Ringing…</p>
            <div className="gcp-primary-actions">
              <button
                type="button"
                className="gcp-btn gcp-btn--decline"
                onClick={handleDecline}
                aria-label="Decline call"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                  call_end
                </span>
                Decline
              </button>
              <button
                type="button"
                className="gcp-btn gcp-btn--answer"
                onClick={handleAnswer}
                aria-label="Answer call"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                  call
                </span>
                Answer
              </button>
            </div>
          </>
        )}

        {/* ── OUTBOUND_CALLING: animated dots + Cancel ── */}
        {popupState === POPUP_STATES.OUTBOUND_CALLING && (
          <>
            <div className="gcp-status-line">
              <span>Calling</span>
              <span className="gcp-dots">
                <span /><span /><span />
              </span>
            </div>
            <div className="gcp-primary-actions">
              <button
                type="button"
                className="gcp-btn gcp-btn--cancel"
                onClick={handleCancelOutbound}
                aria-label="Cancel outbound call"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                  call_end
                </span>
                Cancel
              </button>
            </div>
          </>
        )}

        {/* ── CONNECTED: timer + Mute / Hold / End ── */}
        {popupState === POPUP_STATES.CONNECTED && (
          <>
            <div className="gcp-timer">
              <div className="gcp-timer-dot" />
              <span className="gcp-timer-value">{formatDuration(elapsed)}</span>
            </div>

            <div className="gcp-controls">
              {/* Mute */}
              <button
                type="button"
                className={`gcp-ctrl-btn ${isMuted ? 'gcp-ctrl-btn--active' : ''}`}
                onClick={handleToggleMute}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                <span className="material-symbols-outlined">
                  {isMuted ? 'mic_off' : 'mic'}
                </span>
                {isMuted ? 'Unmute' : 'Mute'}
              </button>

              {/* End Call */}
              <button
                type="button"
                className="gcp-ctrl-btn gcp-ctrl-btn--end"
                onClick={handleEnd}
                aria-label="End call"
                title="End call"
              >
                <span className="material-symbols-outlined">call_end</span>
                End
              </button>

              {/* Hold */}
              <button
                type="button"
                className={`gcp-ctrl-btn ${isOnHold ? 'gcp-ctrl-btn--active' : ''}`}
                onClick={handleToggleHold}
                aria-label={isOnHold ? 'Resume' : 'Hold'}
                title={isOnHold ? 'Resume' : 'Hold'}
              >
                <span className="material-symbols-outlined">
                  {isOnHold ? 'play_arrow' : 'pause'}
                </span>
                {isOnHold ? 'Resume' : 'Hold'}
              </button>
            </div>

            <button
              type="button"
              className="gcp-open-link"
              onClick={handleOpenCallCenter}
            >
              Open Call Center ↗
            </button>
          </>
        )}

        {/* ── MISSED: brief label ── */}
        {popupState === POPUP_STATES.MISSED && (
          <p className="gcp-missed-label">Missed Call</p>
        )}
      </div>
    </div>
  )
}
